// frontend/src/pages/Clients.js
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Users, Search, Plus, Building2, ChevronRight,
  Archive, X, Pencil, Check, Phone, Mail,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('lawliet_token')}` };
}

const EMPTY_FORM = {
  name: '', legal_form: '', rcs: '', siren: '', siret: '', capital: '',
  address: '', postal_code: '', city: '', country: 'France',
  representative_name: '', representative_title: '', representative_email: '',
  representative_phone: '', designation: '', tva_number: '', activity: '', notes: '',
};

const LEGAL_FORMS = ['SAS', 'SARL', 'SA', 'SCI', 'SASU', 'EURL', 'SNC', 'Association', 'Auto-entrepreneur', 'Autre'];

// ── Skeleton ──────────────────────────────────────────────────────────────────
const SkeletonRow = () => (
  <tr className="border-b border-slate-100">
    {[1,2,3,4,5,6].map(i => (
      <td key={i} className="px-4 py-3">
        <div className="h-4 animate-pulse rounded bg-slate-100" />
      </td>
    ))}
  </tr>
);

// ── Create / Edit modal ───────────────────────────────────────────────────────
function ClientModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.siren || !form.representative_name) {
      toast.error('Remplissez les champs obligatoires (*)');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/clients/${initial.id}`, form, { headers: authHeaders() });
        toast.success('Client mis à jour');
      } else {
        await axios.post(`${API}/clients`, form, { headers: authHeaders() });
        toast.success('Client créé');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, k, required, type = 'text', half }) => (
    <div className={half ? '' : 'col-span-2'}>
      <Label className="mb-1 block text-xs font-medium text-slate-600">
        {label}{required && <span className="ml-0.5 text-red-500">*</span>}
      </Label>
      <Input
        type={type}
        value={form[k]}
        onChange={e => set(k, e.target.value)}
        className="h-8 text-sm"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">
            {isEdit ? 'Modifier le client' : 'Nouveau client'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Identification */}
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Identification</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Raison sociale" k="name" required />
              <div>
                <Label className="mb-1 block text-xs font-medium text-slate-600">Forme juridique<span className="ml-0.5 text-red-500">*</span></Label>
                <select
                  value={form.legal_form}
                  onChange={e => set('legal_form', e.target.value)}
                  className="h-8 w-full rounded-md border border-slate-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Sélectionner…</option>
                  {LEGAL_FORMS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <Field label="SIREN" k="siren" required half />
              <Field label="SIRET" k="siret" half />
              <Field label="RCS" k="rcs" required half />
              <Field label="Capital social" k="capital" required half />
              <Field label="N° TVA intracommunautaire" k="tva_number" half />
              <Field label="Secteur d'activité" k="activity" half />
            </div>
          </div>

          {/* Adresse */}
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Adresse du siège</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Adresse" k="address" required />
              <Field label="Code postal" k="postal_code" half />
              <Field label="Ville" k="city" half />
            </div>
          </div>

          {/* Représentant */}
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Représentant légal</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nom complet" k="representative_name" required half />
              <Field label="Fonction" k="representative_title" required half />
              <Field label="Email" k="representative_email" type="email" half />
              <Field label="Téléphone" k="representative_phone" half />
            </div>
          </div>

          {/* Divers */}
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Divers</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Désignation contractuelle" k="designation" half />
              <div className="col-span-2">
                <Label className="mb-1 block text-xs font-medium text-slate-600">Notes internes</Label>
                <textarea
                  value={form.notes}
                  onChange={e => set('notes', e.target.value)}
                  rows={2}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Enregistrement…' : isEdit ? 'Mettre à jour' : 'Créer le client'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Detail slide-over ─────────────────────────────────────────────────────────
function ClientDetail({ client, onClose, onEdit, onArchive }) {
  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
          <div>
            <h2 className="font-semibold text-slate-900">{client.name}</h2>
            <p className="text-xs text-slate-500">{client.legal_form} · SIREN {client.siren}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="flex-1" onClick={onEdit}>
              <Pencil size={13} className="mr-1" /> Modifier
            </Button>
            {client.is_active && (
              <Button size="sm" variant="outline" className="flex-1 text-slate-500" onClick={onArchive}>
                <Archive size={13} className="mr-1" /> Archiver
              </Button>
            )}
          </div>

          {[
            { title: 'Identification', rows: [
              ['Raison sociale', client.name],
              ['Forme juridique', client.legal_form],
              ['SIREN', client.siren],
              ['SIRET', client.siret],
              ['RCS', client.rcs],
              ['Capital', client.capital],
              ['TVA', client.tva_number],
              ['Activité', client.activity],
            ]},
            { title: 'Adresse', rows: [
              ['Adresse', client.address],
              ['Ville', [client.postal_code, client.city].filter(Boolean).join(' ')],
              ['Pays', client.country],
            ]},
            { title: 'Représentant légal', rows: [
              ['Nom', client.representative_name],
              ['Fonction', client.representative_title],
              ['Email', client.representative_email],
              ['Téléphone', client.representative_phone],
            ]},
          ].map(section => (
            <div key={section.title}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{section.title}</p>
              <div className="space-y-1.5 rounded-lg border border-slate-100 bg-slate-50 p-3">
                {section.rows.filter(([, v]) => v).map(([label, value]) => (
                  <div key={label} className="flex justify-between text-sm">
                    <span className="text-slate-500">{label}</span>
                    <span className="font-medium text-slate-800 text-right max-w-[55%] truncate">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {client.notes && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Notes</p>
              <p className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-700">{client.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Clients({ user, cabinet, onLogout }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (showInactive) params.set('is_active', 'false');
      const { data } = await axios.get(`${API}/clients?${params}`, { headers: authHeaders() });
      setClients(data.clients || []);
    } catch {
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  }, [search, showInactive]);

  useEffect(() => { load(); }, [load]);

  const handleArchive = async (client) => {
    if (!window.confirm(`Archiver "${client.name}" ?`)) return;
    try {
      await axios.delete(`${API}/clients/${client.id}`, { headers: authHeaders() });
      toast.success('Client archivé');
      setSelectedClient(null);
      load();
    } catch {
      toast.error('Erreur');
    }
  };

  const openEdit = (client) => {
    setEditClient(client);
    setSelectedClient(null);
    setShowModal(true);
  };

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'EB Garamond, serif' }}>
              Clients
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {clients.length} client{clients.length !== 1 ? 's' : ''} dans votre cabinet
            </p>
          </div>
          <Button onClick={() => { setEditClient(null); setShowModal(true); }} className="flex items-center gap-2">
            <Plus size={15} /> Nouveau client
          </Button>
        </div>

        {/* Filters */}
        <div className="mb-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher par nom, SIREN, représentant…"
              className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={e => setShowInactive(e.target.checked)}
              className="rounded"
            />
            Afficher archivés
          </label>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Raison sociale</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Forme</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">SIREN</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Représentant</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600">Statut</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [1,2,3].map(i => <SkeletonRow key={i} />)
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center text-slate-400">
                    <Building2 className="mx-auto mb-3 h-10 w-10 text-slate-200" />
                    <p className="font-medium text-slate-600">Aucun client</p>
                    <p className="mt-1 text-xs">Créez votre premier client pour commencer.</p>
                    <Button size="sm" className="mt-4" onClick={() => setShowModal(true)}>
                      <Plus size={13} className="mr-1" /> Nouveau client
                    </Button>
                  </td>
                </tr>
              ) : (
                clients.map(client => (
                  <tr key={client.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSelectedClient(client)}>
                    <td className="px-4 py-3 font-medium text-slate-900">{client.name}</td>
                    <td className="px-4 py-3 text-slate-600">{client.legal_form}</td>
                    <td className="px-4 py-3 font-mono text-slate-600">{client.siren}</td>
                    <td className="px-4 py-3 text-slate-600">
                      <div className="flex flex-col">
                        <span>{client.representative_name}</span>
                        {client.representative_email && (
                          <span className="text-xs text-slate-400">{client.representative_email}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                        client.is_active
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-500'
                      }`}>
                        {client.is_active
                          ? <><Check size={10} /> Actif</>
                          : <><Archive size={10} /> Archivé</>
                        }
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => openEdit(client)}
                          className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                          title="Modifier"
                        >
                          <Pencil size={13} />
                        </button>
                        {client.is_active && (
                          <button
                            onClick={() => handleArchive(client)}
                            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-500"
                            title="Archiver"
                          >
                            <Archive size={13} />
                          </button>
                        )}
                        <button
                          onClick={() => setSelectedClient(client)}
                          className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600"
                          title="Voir le détail"
                        >
                          <ChevronRight size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showModal && (
        <ClientModal
          initial={editClient}
          onClose={() => { setShowModal(false); setEditClient(null); }}
          onSaved={() => { setShowModal(false); setEditClient(null); load(); }}
        />
      )}
      {selectedClient && (
        <ClientDetail
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
          onEdit={() => openEdit(selectedClient)}
          onArchive={() => handleArchive(selectedClient)}
        />
      )}
    </Layout>
  );
}
